import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  message,
  Space,
  Card,
  Typography,
  Input,
  InputNumber,
  Select,
  Modal,
  Form,
  Switch,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import {
  getLmdRates,
  getLmdCountries,
  updateLmdRate,
  type LmdRate,
} from "../../services/lmdRatesService";

const { Title, Text } = Typography;
const { Option } = Select;

const LMDRatesTab: React.FC = () => {
  const [rates, setRates] = useState<LmdRate[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>("NL");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRate, setEditingRate] = useState<LmdRate | null>(null);
  const [form] = Form.useForm();

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      const [countryList, ratesList] = await Promise.all([
        getLmdCountries(),
        getLmdRates(selectedCountry),
      ]);
      setCountries(countryList);
      setRates(ratesList);
    } catch (error) {
      console.error("Error loading LMD data:", error);
      message.error("Failed to load LMD rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedCountry]);

  const handleEdit = (rate: LmdRate) => {
    setEditingRate(rate);
    form.setFieldsValue({
      rate_eur: rate.rate_eur,
      is_active: rate.is_active,
      notes: rate.notes,
    });
    setEditModalVisible(true);
  };

  const handleSave = async (values: {
    rate_eur: number;
    is_active: boolean;
    notes?: string;
  }) => {
    if (!editingRate) return;

    try {
      await updateLmdRate(editingRate.id, {
        rate_eur: values.rate_eur,
        is_active: values.is_active,
        notes: values.notes,
      });

      message.success("LMD rate updated successfully");
      setEditModalVisible(false);
      setEditingRate(null);
      form.resetFields();
      loadData();
    } catch (error) {
      console.error("Error updating LMD rate:", error);
      message.error("Failed to update LMD rate");
    }
  };

  const columns = [
    {
      title: "Pallets",
      dataIndex: "pallets",
      key: "pallets",
      width: 80,
      sorter: (a: LmdRate, b: LmdRate) => a.pallets - b.pallets,
    },
    {
      title: "Rate (EUR)",
      dataIndex: "rate_eur",
      key: "rate_eur",
      width: 120,
      render: (value: number) => `€${value.toFixed(2)}`,
      sorter: (a: LmdRate, b: LmdRate) => a.rate_eur - b.rate_eur,
    },
    {
      title: "Rate Type",
      dataIndex: "rate_type",
      key: "rate_type",
      width: 100,
      render: (value: string) => (
        <span
          style={{
            color: value === "ex_diesel" ? "#1890ff" : "#52c41a",
            fontWeight: "bold",
          }}
        >
          {value === "ex_diesel" ? "Ex Diesel" : "Incl Diesel"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (active: boolean) => (
        <span style={{ color: active ? "#52c41a" : "#ff4d4f" }}>
          {active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: unknown, record: LmdRate) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
          size="small"
          type="link"
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <Title level={4} style={{ margin: 0 }}>
              🚚 Last Mile Delivery Rates
            </Title>
            <Text type="secondary">
              Manage tiered transmission rates for different countries
            </Text>
          </Space>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Space>
            <Text strong>Country:</Text>
            <Select
              value={selectedCountry}
              onChange={setSelectedCountry}
              style={{ width: 200 }}
            >
              {countries.map((country) => (
                <Option key={country} value={country}>
                  {country === "NL"
                    ? "🇳🇱 Netherlands"
                    : country === "BE"
                    ? "🇧🇪 Belgium"
                    : country}
                </Option>
              ))}
            </Select>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={rates}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} rates`,
          }}
          scroll={{ y: 400 }}
        />

        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            💡 These rates are used for automatic LMD cost calculation in
            invoice forms. All rates are ex-diesel by default. Diesel surcharge
            is applied separately.
          </Text>
        </div>
      </Card>

      {/* Edit Modal */}
      <Modal
        title={`Edit LMD Rate - ${selectedCountry} (${editingRate?.pallets} pallets)`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingRate(null);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="rate_eur"
            label="Rate (EUR)"
            rules={[
              { required: true, message: "Please enter the rate" },
              { type: "number", min: 0, message: "Rate must be positive" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              precision={2}
              min={0}
              step={0.01}
              formatter={(value) => `€ ${value}`}
              parser={(value) =>
                Number(value!.replace(/€\s?|(,*)/g, "")) as any
              }
            />
          </Form.Item>

          <Form.Item name="is_active" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Optional notes..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setEditingRate(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LMDRatesTab;
